// "Por pagar" y el saldo de la ficha: motivos, estados de los depósitos y filas de cada sección.
import type { RetailCommission } from '@fiado/type-kit';
import { CommissionPaymentAttentionEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionPaymentAttentionEnum';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import { formatMoneyCents } from '@/shared/lib/format/money';
import type { PillTone } from '@/shared/patterns/Pills';
import type { CommissionDirectory } from '../api/useCommissionDirectory';

type Balance = RetailCommission.CommissionSellerBalance;
type Payment = RetailCommission.CommissionPayment;
type Attention = `${RetailCommission.CommissionPaymentAttentionEnum}`;
type PaymentStatus = `${RetailCommission.CommissionPaymentStatusEnum}`;

export type DueSection = 'pending' | 'review' | 'debts';

const ATTENTION_LABEL: Record<Attention, string> = {
    NO_WALLET: 'Sin wallet',
    REJECTED: 'Depósito rechazado',
    PAUSED: 'Pagos pausados',
    DEBT: 'Con deuda',
};

/** Los rechazos del motor de cobro que se nombran; los demás quedan como "Depósito rechazado". */
const REJECTION_LABEL: Partial<Record<string, string>> = {
    ACCOUNT_NOT_FOUND: 'Cuenta no encontrada',
    USER_NOT_ACTIVE: 'Cuenta bloqueada',
    ACCOUNT_LEVEL_MAX_MONTHLY_AMOUNT_EXCEEDED: 'Tope de nivel',
    ACCOUNT_LEVEL_MAX_AVAILABLE_BALANCE_EXCEEDED: 'Tope de nivel',
};

export function attentionText(balance: Pick<Balance, 'attention' | 'attentionCode'>): string {
    const { attention, attentionCode } = balance;
    if (attention === null) return 'Sin motivo';
    if (attention === CommissionPaymentAttentionEnum.REJECTED && attentionCode !== null)
        return REJECTION_LABEL[attentionCode] ?? ATTENTION_LABEL.REJECTED;
    return ATTENTION_LABEL[attention];
}

export const PAYMENT_STATUS_LABEL: Record<PaymentStatus, string> = {
    PENDING: 'En curso',
    ACCREDITED: 'Depositado',
    FAILED: 'Rechazado',
    UNVERIFIED: 'Sin confirmar',
};

export const PAYMENT_STATUS_TONE: Record<PaymentStatus, PillTone> = {
    PENDING: 'info',
    ACCREDITED: 'success',
    FAILED: 'danger',
    UNVERIFIED: 'warning',
};

/** Por qué un depósito quedó sin confirmar, sin el código crudo del motor de cobro. */
export function unverifiedReason(code: string | null): string {
    if (code === null) return 'El motor de cobro no dio un código.';
    if (code === 'DEPOSIT_NOT_DELIVERED')
        return 'El motor de cobro no recibió el último intento; uno anterior pudo haber depositado. Confírmalo en PCF antes de decidir.';
    if (code === 'NO_RESPONSE') return 'El motor de cobro no respondió.';
    const status = /^HTTP_(\d{3})$/.exec(code)?.[1];
    if (status !== undefined)
        return status.startsWith('5')
            ? `El motor de cobro respondió con error (HTTP ${status}) sin decir si depositó.`
            : `El motor de cobro respondió HTTP ${status} sin decir si depositó.`;
    return `Código del motor de cobro: ${code}`;
}

export interface DueRow {
    /** Único en su sección: el vendedor o el depósito. */
    key: string;
    section: DueSection;
    sellerId: string;
    sellerName: string;
    storeId: string | null;
    storeName: string;
    /** Lo pendiente, el monto del depósito o lo que debe, siempre en positivo. */
    amountCents: number;
    reason: string;
    tone: PillTone;
    since: string | null;
    balance: Balance | null;
    payment: Payment | null;
}

export interface DueRows {
    pending: DueRow[];
    review: DueRow[];
    debts: DueRow[];
}

export type DueNames = Pick<CommissionDirectory, 'sellerName' | 'sellerStoreId' | 'storeName'>;

function sellerOf(sellerId: string, names: DueNames) {
    const storeId = names.sellerStoreId(sellerId);
    return {
        sellerId,
        sellerName: names.sellerName(sellerId),
        storeId,
        storeName: storeId === null ? 'Sin tienda' : names.storeName(storeId),
    };
}

function balanceRow(section: 'pending' | 'debts', balance: Balance, names: DueNames): DueRow {
    const debt = section === 'debts';
    return {
        key: `${section}:${balance.sellerId}`,
        section,
        ...sellerOf(balance.sellerId, names),
        amountCents: debt ? Math.abs(balance.payableCents) : balance.payableCents,
        reason: debt ? 'Debe' : attentionText(balance),
        tone: debt ? 'danger' : 'warning',
        since: balance.attentionSince,
        balance,
        payment: null,
    };
}

function paymentRow(payment: Payment, names: DueNames): DueRow {
    return {
        key: `review:${payment.paymentId}`,
        section: 'review',
        ...sellerOf(payment.sellerId, names),
        amountCents: payment.amountCents,
        reason: PAYMENT_STATUS_LABEL.UNVERIFIED,
        tone: 'warning',
        since: payment.requestedAt,
        balance: null,
        payment,
    };
}

const byAmount = (a: DueRow, b: DueRow) =>
    b.amountCents - a.amountCents || a.sellerName.localeCompare(b.sellerName, 'es-MX');

/** Pendientes y deudas, del mayor al menor; depósitos por revisar, del más viejo al más nuevo. */
export function buildDueRows(
    attention: RetailCommission.GetPaymentAttentionResponse | undefined,
    names: DueNames,
): DueRows {
    if (attention === undefined) return { pending: [], review: [], debts: [] };
    return {
        pending: attention.pending.map((b) => balanceRow('pending', b, names)).sort(byAmount),
        review: attention.unverified
            .map((p) => paymentRow(p, names))
            .sort((a, b) => (a.since ?? '').localeCompare(b.since ?? '')),
        debts: attention.debts.map((b) => balanceRow('debts', b, names)).sort(byAmount),
    };
}

export function filterDueRows(
    rows: readonly DueRow[],
    filter: { search: string; storeId: string },
): DueRow[] {
    const query = filter.search.trim().toLowerCase();
    return rows.filter(
        (row) =>
            (filter.storeId === '' || row.storeId === filter.storeId) &&
            (query === '' ||
                row.sellerName.toLowerCase().includes(query) ||
                row.sellerId.toLowerCase().includes(query)),
    );
}

export function totalCentsOf(rows: readonly DueRow[]): number {
    return rows.reduce((sum, row) => sum + row.amountCents, 0);
}

export interface DetailLine {
    label: string;
    value: string;
}

/** El saldo en sus bolsas, o el depósito con lo que dijo el motor de cobro. */
export function detailLines(row: DueRow): DetailLine[] {
    if (row.payment) {
        const p = row.payment;
        return [
            { label: 'Monto', value: formatMoneyCents(p.amountCents) },
            { label: 'Solicitado', value: formatDateTimeMx(p.requestedAt) },
            { label: 'Concepto', value: p.concept },
            { label: 'Qué pasó', value: unverifiedReason(p.errorCode) },
        ];
    }
    if (!row.balance) return [];
    const b = row.balance;
    return [
        { label: 'Por depositar', value: formatMoneyCents(b.payableCents) },
        { label: 'Retenido para fin de mes', value: formatMoneyCents(b.retainedCents) },
        { label: 'En tránsito', value: formatMoneyCents(b.inTransitCents) },
        { label: 'Ganado', value: formatMoneyCents(b.earnedCents) },
        { label: 'Descontado', value: formatMoneyCents(b.deductedCents) },
        { label: 'Depositado', value: formatMoneyCents(b.accreditedCents) },
        { label: 'Pagado por fuera', value: formatMoneyCents(b.paidOutsideCents) },
    ];
}

/** Los depósitos más recientes primero. */
export function recentPayments(payments: readonly Payment[], limit: number): Payment[] {
    return [...payments].sort((a, b) => b.requestedAt.localeCompare(a.requestedAt)).slice(0, limit);
}
